import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import ImageUploadField from '../../../components/shared/ImageUploadField';
import { Plus, Trash2, Pencil, Check, X, Tag } from 'lucide-react';

export default function CategoriasPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNome, setNewNome] = useState('');
  const [newMinimo, setNewMinimo] = useState(0);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ nome: '', imagem_url: '', pedido_minimo: 0 });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const loadCategorias = () => {
    setLoading(true);
    api.get('/categorias').then(r => setCategorias(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { loadCategorias(); }, []);

  const handleCreate = async () => {
    if (!newNome.trim()) return;
    setSaving(true);
    setError('');
    try {
      const params = new URLSearchParams({ nome: newNome.trim(), pedido_minimo: newMinimo || 0 });
      await api.post(`/categorias?${params}`);
      setNewNome('');
      setNewMinimo(0);
      loadCategorias();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao criar categoria');
    } finally { setSaving(false); }
  };

  const startEdit = (cat) => {
    setEditId(cat.id);
    setEditData({ nome: cat.nome, imagem_url: cat.imagem_url || '', pedido_minimo: cat.pedido_minimo || 0 });
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    setError('');
    try {
      const params = new URLSearchParams({
        nome: editData.nome,
        imagem_url: editData.imagem_url,
        pedido_minimo: editData.pedido_minimo || 0,
      });
      await api.put(`/categorias/${editId}?${params}`);
      setEditId(null);
      loadCategorias();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/categorias/${deleteTarget.id}`);
      setDeleteTarget(null);
      loadCategorias();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao excluir');
    } finally { setDeleting(false); }
  };

  return (
    <div data-testid="categorias-page">
      {/* Delete modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-dashboard font-bold text-lg text-brand-text mb-2">Confirmar exclusão</h3>
            <p className="text-sm font-body text-brand-text-muted mb-5">
              Excluir a categoria <strong>"{deleteTarget.nome}"</strong>? Apenas categorias sem produtos podem ser excluídas.
            </p>
            <div className="flex gap-3">
              <button onClick={() => { setDeleteTarget(null); setError(''); }} className="flex-1 py-2 rounded-lg border border-brand-border text-sm font-dashboard">Cancelar</button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2 rounded-lg bg-red-600 text-white text-sm font-dashboard font-semibold disabled:opacity-60">{deleting ? 'Excluindo...' : 'Excluir'}</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="dashboard-heading text-2xl">Categorias de Produto</h1>
          <p className="text-brand-text-muted font-body text-sm">{categorias.length} categoria(s)</p>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">{error}</div>}

      {/* Nova categoria */}
      <div className="card-dashboard mb-5">
        <h3 className="font-dashboard font-semibold text-brand-text mb-3">Nova Categoria</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="block text-xs text-brand-text-muted mb-1 font-body">Nome da Categoria *</label>
            <input
              className="w-full rounded-lg border border-brand-border px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-brand-accent-mid"
              placeholder="Ex: Pães, Doces, Salgados..."
              value={newNome}
              onChange={e => setNewNome(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
              data-testid="input-nova-categoria"
            />
          </div>
          <div>
            <label className="block text-xs text-brand-text-muted mb-1 font-body">Qtd. Mínima no Pedido</label>
            <input
              type="number"
              min="0"
              className="w-full rounded-lg border border-brand-border px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-brand-accent-mid"
              placeholder="0 = sem mínimo"
              value={newMinimo || ''}
              onChange={e => setNewMinimo(parseInt(e.target.value) || 0)}
              data-testid="input-nova-categoria-minimo"
            />
          </div>
        </div>
        <button
          onClick={handleCreate}
          disabled={saving || !newNome.trim()}
          className="mt-3 flex items-center gap-2 px-5 py-2.5 rounded-lg btn-primary font-dashboard font-semibold text-sm disabled:opacity-50"
          data-testid="btn-criar-categoria"
        >
          <Plus className="w-4 h-4" />
          {saving ? 'Criando...' : 'Criar'}
        </button>
      </div>

      {/* Lista */}
      <div className="card-dashboard">
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : categorias.length === 0 ? (
          <div className="text-center py-12">
            <Tag className="w-10 h-10 text-brand-text-muted/30 mx-auto mb-3" strokeWidth={1} />
            <p className="text-brand-text-muted text-sm font-body">Nenhuma categoria cadastrada</p>
          </div>
        ) : (
          <div className="space-y-0">
            {categorias.map((cat, i) => (
              <div
                key={cat.id}
                className={`flex items-center gap-4 py-3.5 ${i < categorias.length - 1 ? 'border-b border-brand-border' : ''}`}
                data-testid="categoria-row"
              >
                {/* Imagem */}
                <div className="w-12 h-12 rounded-xl bg-brand-bg border border-brand-border overflow-hidden flex-shrink-0 flex items-center justify-center">
                  {cat.imagem_url ? (
                    <img src={cat.imagem_url} alt={cat.nome} className="w-full h-full object-cover" onError={e => { e.target.style.display='none'; }} />
                  ) : (
                    <Tag className="w-5 h-5 text-brand-text-muted/30" strokeWidth={1.5} />
                  )}
                </div>

                {/* Edit mode */}
                {editId === cat.id ? (
                  <div className="flex-1 space-y-3">
                    <input
                      className="w-full rounded-lg border border-brand-border px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                      value={editData.nome}
                      onChange={e => setEditData(d => ({ ...d, nome: e.target.value }))}
                      data-testid="edit-categoria-nome"
                    />
                    <div>
                      <label className="block text-xs text-brand-text-muted mb-1 font-body">Qtd. Mínima no Pedido</label>
                      <input
                        type="number"
                        min="0"
                        className="w-32 rounded-lg border border-brand-border px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                        placeholder="0 = sem mínimo"
                        value={editData.pedido_minimo || ''}
                        onChange={e => setEditData(d => ({ ...d, pedido_minimo: parseInt(e.target.value) || 0 }))}
                        data-testid="edit-categoria-minimo"
                      />
                    </div>
                    <ImageUploadField
                      value={editData.imagem_url}
                      onChange={url => setEditData(d => ({ ...d, imagem_url: url }))}
                      placeholder="Adicionar imagem"
                      size="sm"
                    />
                    <div className="flex gap-2">
                      <button onClick={handleSaveEdit} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg btn-primary text-xs font-dashboard font-semibold" data-testid="save-categoria-btn">
                        <Check className="w-3.5 h-3.5" /> {saving ? 'Salvando...' : 'Salvar'}
                      </button>
                      <button onClick={() => setEditId(null)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-brand-border text-xs font-dashboard text-brand-text-muted">
                        <X className="w-3.5 h-3.5" /> Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <p className="font-dashboard font-medium text-brand-text text-sm">{cat.nome}</p>
                      <p className="text-xs text-brand-text-muted font-body mt-0.5">
                        {cat.pedido_minimo > 0 ? `Mín. ${cat.pedido_minimo} un. por pedido` : 'Sem mínimo'}
                        {!cat.imagem_url && ' · Sem imagem'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => startEdit(cat)}
                        className="p-2 rounded-lg hover:bg-brand-bg text-brand-text-muted hover:text-brand-primary transition-colors"
                        title="Editar"
                        data-testid="edit-categoria-btn"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => { setDeleteTarget(cat); setError(''); }}
                          className="p-2 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                          title="Excluir"
                          data-testid="delete-categoria-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
